import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { profile } from 'api';
import moment from 'moment';
import { RootState } from 'store';

import { fetchGroup } from 'actions/group';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import useSelectionOrigin from 'hooks/useSelectionOrigin';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { AdminUser } from 'models/adminUser';
import { Group } from 'models/group';
import GroupForm from 'screens/components/Group';
import { saveGroupsCard } from 'services/paymentMethodService';

interface Params {
  match: {
    params: {
      groupId: string;
    };
  };
  originRule?: string;
  isFromSelection?: boolean;
  onCloseFormGroup?: () => void;
}

const GroupFormContainer = ({ match: { params }, onCloseFormGroup, isFromSelection, originRule }: Params) => {
  const toast = useToast();
  const { t } = useStoreTranslation();

  const [isLoading, setIsLoading] = useState(!!params.groupId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [group, setGroup] = useState<Group>();
  const fromOrigin = useSelectionOrigin();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const history = useHistory();

  const { accountResult } = useSelector((state: RootState) => state.user);
  if (!accountResult) return null;
  const { companyId, countryCurrency } = accountResult;

  const loadGroup = useCallback(async () => {
    try {
      if (params.groupId && isLoading) {
        const {
          data: { data },
        } = await fetchGroup(params.groupId);

        setGroup({
          ...data,
          cardDueDate: moment(data.cardDueDate).format('MM/YY'),
          users: data.users.map((user: AdminUser) => ({
            ...user,
            userGroupName: data.name,
          })),
        });
      }
    } catch (error) {
      toast.add({ type: 'danger', message: 'Error' });
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, params.groupId, toast]);

  useEffect(() => {
    loadGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveGroup = async (group: Group) => {
    setIsSubmitting(true);
    try {
      if (group.id) {
        await profile.put(`/usergroup/${group.id}`, {
          ...group,
          companyId,
        });
        setIsSubmitting(false);
        return;
      }
      const {
        data: { data },
      } = await profile.post('/usergroup', {
        ...group,
        companyId,
      });

      // paymentCardId == '' is the default card, don't need to link it to the group.
      if (group.paymentCardId && group.paymentCardId !== '') {
        try {
          await saveGroupsCard(group.paymentCardId, [data.id]);
          setIsSubmitting(false);
        } catch (e) {
          setIsSubmitting(false);
          throw e;
        }
        return;
      }
      setIsSubmitting(false);
      if (isFromSelection) {
        fromOrigin.updateItemCreated([data]);
        return;
      }
    } catch (e) {
      setIsSubmitting(false);
      throw e;
    }
  };

  const preHandleDelete = async () => {
    setShowConfirmDelete(true);
  };

  const handleOnDelete = async (userGroupId: string) => {
    setIsSubmitting(true);

    try {
      if (userGroupId) {
        const {
          data: { errors, succeeded },
        } = await profile.delete(`/usergroup/${userGroupId}`);

        const errorsDescription = [];

        if (errors) {
          //Flujo con Usuarios para Mover
          if (errors.includes('UserGroupHasUsers')) {
            errorsDescription.push(t('group.errors.userGroupHasUsers'));
          }

          //Flujo con Viajes Pendientes
          if (errors.includes('UserGroupHasPendingTravels')) {
            errorsDescription.push(t('group.errors.userGroupHasPendingTravels'));
          }

          if (errorsDescription.length > 0) {
            toast.add({
              type: 'danger',
              message: `${t('group.errors.prefixMessage')} ${errorsDescription.join('y, ')}`,
            });
          }
        }

        if (succeeded) {
          //Flujo Exitoso
          toast.add({
            type: 'success',
            message: t('group.delete.success'),
          });

          history.push('/corporate/groupList');
        }
        setIsSubmitting(false);
        return;
      }
    } catch (e) {
      setIsSubmitting(false);
      throw e;
    }
  };

  return (
    <>
      <GroupForm
        originRule={originRule}
        isFromSelection={isFromSelection}
        loading={isLoading}
        submitting={isSubmitting}
        group={group}
        onConfirm={handleSaveGroup}
        countryCurrency={countryCurrency}
        onCloseFormGroup={onCloseFormGroup}
        onDelete={preHandleDelete}
      />
      <ConfirmBottomSheet
        show={showConfirmDelete}
        title={t('group.delete.bottomSheetTitle')}
        onClose={() => setShowConfirmDelete(false)}
        content={t('group.delete.bottomSheetContent')}
        confirmText={t('group.delete.bottomSheetButton')}
        onConfirm={() => handleOnDelete(group?.id || '')}
      />
    </>
  );
};

export default GroupFormContainer;
